/**
 * @file DeliveryQuoteInline.tsx
 * @description Блок «Расчёт перевозчика» внутри вкладки «Доставка»
 * (phase19.24 шаг 5.2, §3 и уточнение пользователя §8).
 *
 * Расчёт вызывается прямо отсюда, а не из «Отгрузок»: тариф определяет сумму,
 * которую подтверждает клиент, и менеджеру некуда за ней ходить. Выбранный
 * тариф подставляет стоимость для клиента — её можно поправить руками, и
 * тогда снапшот остаётся расчётным, а сумма честно расходится с тарифом.
 *
 * Показывается ТОЛЬКО для перевозчика с калькулятором в системе (решение
 * пользователя 2026-09-21, сегодня это СДЭК): для остальных о калькуляторе не
 * упоминаем вовсе — там есть поля адреса и ручной стоимости, и плашка «расчёт
 * недоступен» была бы шумом.
 *
 * Сломанная или не настроенная интеграция блок НЕ прячет (правка 2026-09-22):
 * менеджер жмёт «Рассчитать» и читает причину со ссылкой в настройки. Пустой
 * экран вместо кнопки не объясняет ничего и чинится только через поддержку.
 */

import { Calculator, RefreshCw } from "lucide-react";

import type { DeliveryQuoteSnapshot } from "../../../lib/api-delivery-agreement";
import { formatMoney } from "../../../lib/api-orders";
import { formatPeriod } from "../../../lib/delivery-quote";
import { formatShopDateTime } from "../../../lib/shop-time";
import { Button } from "../../ui/button";
import { SectionLabel } from "../../ui/SectionLabel";

interface Props {
  /** Тариф, которым посчитали текущую стоимость; `null` — расчёта не было. */
  quote: DeliveryQuoteSnapshot | null;
  /** Перевозчик по-русски: «СДЭК». */
  carrierName: string;
  /** Стоимость для клиента из формы — её менеджер мог поправить после расчёта. */
  clientPrice: number | null;
  disabled?: boolean;
  onCalculate: () => void;
}

export function DeliveryQuoteInline({
  quote,
  carrierName,
  clientPrice,
  disabled,
  onCalculate,
}: Props) {
  return (
    <section className="flex flex-col gap-2 rounded-xl bg-surface-low px-4 py-3">
      {/* `div`, а не заголовок: секцию вкладки открывает «Что согласуем»,
          и второй `h2` внутри неё ломал бы навигацию озвучки. */}
      <SectionLabel>Расчёт {carrierName}</SectionLabel>

      {quote ? (
        <div className="flex flex-col gap-1 text-sm">
          <p className="font-medium">
            Тариф {quote.tariffCode} · {quote.tariffName}
          </p>
          <p className="flex flex-wrap gap-x-3 gap-y-1">
            <span>
              клиенту{" "}
              <span className="font-semibold">
                {clientPrice === null ? "не выставлено" : `${formatMoney(String(clientPrice))} ₽`}
              </span>
            </span>
            <span className="text-on-surface-variant">
              {/* Цена перевозчика приходит строкой Decimal — печатаем её как
                  все деньги, через formatMoney. */}
              тариф {carrierName} {formatMoney(quote.carrierPrice)} ₽
            </span>
            <span className="text-on-surface-variant">
              {formatPeriod(quote.periodMin ?? null, quote.periodMax ?? null)}
            </span>
          </p>
          <p className="text-xs text-on-surface-variant">
            рассчитано {formatShopDateTime(quote.quotedAt)}
          </p>
        </div>
      ) : (
        <p className="text-sm text-on-surface-variant">
          Тариф подставит стоимость для клиента. Вес и габариты берутся из
          позиций заказа — поправить их можно в окне расчёта.
        </p>
      )}

      <Button
        variant="outline"
        // Тач-цель 56px на телефоне: кнопку жмут из машины и со склада.
        className="min-h-14 self-start lg:min-h-12"
        disabled={disabled}
        onClick={onCalculate}
      >
        {quote ? (
          <RefreshCw className="mr-1 size-4" aria-hidden />
        ) : (
          <Calculator className="mr-1 size-4" aria-hidden />
        )}
        {quote ? "Пересчитать" : "Рассчитать"}
      </Button>
    </section>
  );
}
